
import { __ } from "@wordpress/i18n";
import apiFetch from '@wordpress/api-fetch';
import React, { useEffect, useState } from 'react';
import PageHeading from "../../components/PageHeading/PageHeading";
import Notice from "../Notice/Notice";
import { useMenu } from "../../contexts/MenuContext";
import {  urlToArr, useCurrentPageObject } from "../../lib/Helpers"; // Import utility function
import "./WithForm.scss";

const WithForm = (OriginalComponent, sectionPath = null) => {     
    function NewComponent() {
        const {
            settingData, 
            setSettingData,
            menuData,
        } = useMenu();

        const [count, setCount] = useState(0);
        const [saveLoading, setSaveLoading] = useState(false);
        const [showFormNotice, setShowFormNotice] = useState(false);
        const [activeDiscard, setActiveDiscard] = useState(false);
        const [processing, setProcessing] = useState(false);
        const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
        const [databaseData, setDatabaseData] = useState(null);

        const OPTIONS_API_URL = "access-control/v1/options";

        // Helper to get nested value using path string
        const getNestedValue = (obj, path) => {
            if (!path || !obj) return obj;
            
            const pathParts = path.split('.');
            let result = obj;
            
            for (const part of pathParts) {
                if (result === undefined || result === null || typeof result !== 'object') {
                    return undefined;
                }
                result = result[part];
            }
            
            return result;
        };

        // Helper to set nested value using path string
        const setNestedValue = (obj, path, value) => {
            // Create a deep copy to avoid modifying the original object
            const result = JSON.parse(JSON.stringify(obj));
            
            const pathParts = path.split('.');
            let current = result;
            
            // Navigate to the right depth in the object
            for (let i = 0; i < pathParts.length - 1; i++) {
                if (!current[pathParts[i]]) {
                    current[pathParts[i]] = {};
                }
                current = current[pathParts[i]];
            }
            
            // Set the value at the final path segment
            const lastPart = pathParts[pathParts.length - 1];
            current[lastPart] = value;
            
            return result;
        };

        // Function to check if current data matches database data
        const checkForChanges = (current, original) => {
            // If either is null, we can't compare
            if (!current || !original) return false;
            
            // Deep compare objects
            return JSON.stringify(current) !== JSON.stringify(original);
        };

        // Fetch fresh database data
        const fetchDatabaseData = async () => {
            try {
                let endpoint = OPTIONS_API_URL;
                if (sectionPath) {
                    endpoint += `?section=${sectionPath}`;
                }
                
                const response = await apiFetch({
                    path: endpoint,
                    method: 'GET'
                });
                
                return response;
            } catch (error) {
                console.error(`Error fetching settings:`, error);
                return null;
            }
        };

        // Store the database data when component mounts and when settingData changes
        useEffect(() => {
            const initializeDatabaseData = async () => {
                // For the first load, fetch fresh data directly from the database
                const freshData = await fetchDatabaseData();
                
                if (freshData) {
                    // Store the database data for comparison
                    if (sectionPath) {
                        // For sections, store just the relevant section
                        setDatabaseData(freshData);
                    } else {
                        // For top-level, store all
                        setDatabaseData(freshData);
                    }
                }
            };
            
            initializeDatabaseData();
        }, []); // Only run on mount

        // Continuously monitor for changes between current settingData and database data
        useEffect(() => {
            if (settingData && databaseData) {
                let currentData;
                
                if (sectionPath) {
                    // Get the specific section we're working with
                    currentData = getNestedValue(settingData, sectionPath);
                } else {
                    // For top-level components, use all data
                    currentData = settingData;
                }
                
                // Check if current data differs from database data
                const hasChanges = checkForChanges(currentData, databaseData);
                
                // Update state based on comparison
                setHasUnsavedChanges(hasChanges);
                
                // Update localStorage based on whether there are changes
                if (hasChanges) {
                    if (sectionPath) {
                        localStorage.setItem(`changes_${sectionPath}`, JSON.stringify({
                            changed: true,
                            timestamp: Date.now()
                        }));
                    } else {
                        localStorage.setItem(`changes_all`, JSON.stringify({
                            changed: true,
                            timestamp: Date.now()
                        }));
                    }
                } else {
                    // Remove from localStorage if values match database
                    if (sectionPath) {
                        localStorage.removeItem(`changes_${sectionPath}`);
                    } else {
                        localStorage.removeItem(`changes_all`);
                    }
                }
            }
        }, [settingData, databaseData, sectionPath]);

        // Handle changes from child components
        const handleChange = (fieldPath, value) => {
            if (!fieldPath) {
                console.error("Field path is required");
                return;
            }

            setSettingData(prev => {
                // Determine the full path to update
                let fullPath = fieldPath;
                
                // If sectionPath is provided, construct the full path
                if (sectionPath && !fieldPath.includes(sectionPath)) {
                    fullPath = `${sectionPath}.${fieldPath}`;
                }
                
                // Create updated state
                const updatedState = setNestedValue(prev, fullPath, value);
                
                return updatedState;
            });
            
            setCount((prevCount) => prevCount + 1);
        };

        const handleSave = () => {
            setProcessing(true);
            setSaveLoading(true);
            
            // Create the data structure to save
            const dataToSave = { 'access_control_options': {} };
            
            if (sectionPath) {
                // For nested sections, get the specific section data
                const sectionData = getNestedValue(settingData, sectionPath);
                
                // Build the nested structure for the API
                const pathParts = sectionPath.split('.');
                let current = dataToSave['access_control_options'];
                
                for (let i = 0; i < pathParts.length - 1; i++) {
                    current[pathParts[i]] = {};
                    current = current[pathParts[i]];
                }
                
                // Set the actual data at the final nesting level
                current[pathParts[pathParts.length - 1]] = sectionData;
            } else {
                // For top-level sections, use entire settingData
                dataToSave['access_control_options'] = settingData;
            }
        
            apiFetch({
                path: OPTIONS_API_URL,
                method: 'POST',
                data: dataToSave
            })
            .then(response => {
                window.scrollTo(0, 0);
        
                setSaveLoading(false);
                setProcessing(false);
                setActiveDiscard(false);
                setShowFormNotice(true);
                setHasUnsavedChanges(false);
                
                // Clear changes from localStorage
                if (sectionPath) {
                    localStorage.removeItem(`changes_${sectionPath}`);
                } else {
                    localStorage.removeItem(`changes_all`);
                }
                
                // Update the database data to match the new saved state
                if (sectionPath) {
                    const newSectionData = getNestedValue(settingData, sectionPath);
                    setDatabaseData(JSON.parse(JSON.stringify(newSectionData)));
                } else {
                    setDatabaseData(JSON.parse(JSON.stringify(settingData)));
                }
        
                setTimeout(() => {
                    setShowFormNotice(false);
                }, 3000);
            })
            .catch(error => {
                console.error("Error saving settings:", error);
                setSaveLoading(false);
                setProcessing(false);
            });
        };
        
        const handleDiscard = async () => {
            setActiveDiscard(true);
            
            try {
                // Fetch the current settings from the server
                const freshData = await fetchDatabaseData();
                
                if (freshData) {
                    setSettingData(prev => {
                        const newState = JSON.parse(JSON.stringify(prev));
                    
                        if (sectionPath) {
                            const pathParts = sectionPath.split('.');
                            let current = newState;
                    
                            for (let i = 0; i < pathParts.length - 1; i++) {
                                if (!current[pathParts[i]]) current[pathParts[i]] = {};
                                current = current[pathParts[i]];
                            }
                    
                            const lastPart = pathParts[pathParts.length - 1];
                            current[lastPart] = freshData; // because freshData *is* the section
                    
                        } else {
                            return freshData; // full reset
                        }
                    
                        return newState;
                    });
                
                    // Clear localStorage
                    if (sectionPath) {
                        localStorage.removeItem(`changes_${sectionPath}`);
                    } else {
                        localStorage.removeItem('changes_all');
                    }
                
                    // No need to update databaseData as it's already the database state
                    setHasUnsavedChanges(false);
                }
            } catch (error) {
                console.error("Error discarding changes:", error);
            } finally {
                setActiveDiscard(false);
            }
        };
        
        const urlArr = urlToArr();
        const pageObject = useCurrentPageObject(urlArr, menuData);
        
        return (
            <>
                {showFormNotice && <Notice />}
                <div className="settings-container">
                    <div className="form-container bg-white border">
                        <div className="form-header p-lg border-bottom">
                            <PageHeading 
                                title={pageObject?.title || ""}
                                subtitle={pageObject?.description || ""}
                            />
                        </div>
                        <div className="form-body p-lg border-bottom">
                            <OriginalComponent handleChange={handleChange} /> 
                        </div>
                        <div className="form-footer p-lg">
                            <div className="button-wrapper d-flex align-items-center gap-3">
                                <button 
                                    type="button" 
                                    className="button button-primary" 
                                    onClick={handleSave}
                                    disabled={processing || activeDiscard || !hasUnsavedChanges}
                                >
                                    {saveLoading 
                                        ? __("Saving...", "access-control") 
                                        : __("Save Changes", "access-control")}
                                </button>
                                <button 
                                    type="button" 
                                    className="button button-secondary" 
                                    onClick={handleDiscard}
                                    disabled={processing || activeDiscard || !hasUnsavedChanges}
                                >
                                    {__("Discard Changes", "access-control")}
                                </button>
                                {hasUnsavedChanges && (
                                    <div className="unsaved-changes-indicator">
                                        <span className="text-warning">
                                            <i className="dashicons dashicons-warning"></i> {__("Unsaved changes", "access-control")}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>  
                </div>     
            </>
        );
    }
    return NewComponent;    
};
export default WithForm;